import React, { useEffect, useRef } from 'react';

const ZERO_WIDTH = '\u200b';

function escapeHtml(value) {
    return value
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}

function serializeTelegramHtmlChildren(nodes) {
    return Array.from(nodes).map(serializeTelegramHtmlNode).join('');
}

function serializeTelegramHtmlNode(node) {
    if (node.nodeType === Node.TEXT_NODE) {
        return escapeHtml(node.textContent || '').replace(/\n/g, '<br>');
    }

    if (node.nodeType !== Node.ELEMENT_NODE) {
        return '';
    }

    const tagName = node.tagName.toLowerCase();
    if (tagName === 'br') {
        return '<br>';
    }

    const children = serializeTelegramHtmlChildren(node.childNodes);

    switch (tagName) {
        case 'b':
        case 'strong':
            return `<b>${children}</b>`;
        case 'i':
        case 'em':
            return `<i class="telegram-italic">${children}</i>`;
        case 'u':
            return `<span class="telegram-underline">${children}</span>`;
        case 's':
        case 'del':
        case 'strike':
            return `<del>${children}</del>`;
        case 'blockquote':
            return `<blockquote class="telegram-blockquote">${children}</blockquote>`;
        case 'tg-spoiler':
            return `<span data-tg-spoiler="true" class="telegram-spoiler">${children}</span>`;
        default:
            return children;
    }
}

function formatTelegramToHtml(value) {
    if (!value) {
        return '';
    }

    if (typeof window === 'undefined' || typeof DOMParser === 'undefined') {
        return escapeHtml(value);
    }

    const parser = new DOMParser();
    const doc = parser.parseFromString(`<div>${value}</div>`, 'text/html');
    const root = doc.body.firstElementChild;

    if (!root) {
        return escapeHtml(value);
    }

    return serializeTelegramHtmlChildren(root.childNodes);
}

function isSpoilerElement(node) {
    if (!node || node.nodeType !== Node.ELEMENT_NODE) {
        return false;
    }

    const tagName = node.tagName.toLowerCase();
    if (tagName !== 'span') {
        return false;
    }

    return node.getAttribute('data-tg-spoiler') === 'true' || node.classList.contains('telegram-spoiler');
}

function serializeTelegramTagChildren(nodes) {
    return Array.from(nodes).map((node, index) =>
        serializeTelegramTagNode(node, index === nodes.length - 1)
    ).join('');
}

function serializeTelegramTagNode(node, isLast) {
    if (node.nodeType === Node.TEXT_NODE) {
        return (node.textContent || '').replace(new RegExp(ZERO_WIDTH, 'g'), '');
    }

    if (node.nodeType !== Node.ELEMENT_NODE) {
        return '';
    }

    const tagName = node.tagName.toLowerCase();
    if (tagName === 'br') {
        return '\n';
    }

    const children = serializeTelegramTagChildren(node.childNodes);

    if (tagName === 'div' || tagName === 'p') {
        return isLast ? children : `${children}\n`;
    }

    if (isSpoilerElement(node)) {
        return `<tg-spoiler>${children}</tg-spoiler>`;
    }

    switch (tagName) {
        case 'b':
        case 'strong':
            return `<b>${children}</b>`;
        case 'i':
        case 'em':
            return `<i>${children}</i>`;
        case 'u':
            return `<u>${children}</u>`;
        case 's':
        case 'del':
        case 'strike':
            return `<s>${children}</s>`;
        case 'blockquote':
            return `<blockquote>${children}</blockquote>`;
        default:
            return children;
    }
}

function htmlToTelegramTags(html) {
    if (!html) {
        return '';
    }

    if (typeof window === 'undefined' || typeof DOMParser === 'undefined') {
        return html;
    }

    const parser = new DOMParser();
    const doc = parser.parseFromString(`<div>${html}</div>`, 'text/html');
    const root = doc.body.firstElementChild;

    if (!root) {
        return '';
    }

    return serializeTelegramTagChildren(root.childNodes);
}

export function TextForm({ value = '', onChange, hint = '', showVariables = true, variables = [], placeholder = 'Olá! Bem-vindo ao meu bot! Escolha um valor abaixo para fazer seu PIX:' }) {
    const editorRef = useRef(null);
    const isFocusedRef = useRef(false);
    const selectionRef = useRef(null);

    useEffect(() => {
        if (!editorRef.current || isFocusedRef.current) {
            return;
        }
        editorRef.current.innerHTML = formatTelegramToHtml(value);
    }, [value]);

    const captureSelection = () => {
        const selection = window.getSelection();
        if (!selection || selection.rangeCount === 0) {
            return;
        }

        const range = selection.getRangeAt(0);
        if (!editorRef.current || !editorRef.current.contains(range.commonAncestorContainer)) {
            return;
        }

        selectionRef.current = range;
    };

    const restoreSelection = () => {
        const selection = window.getSelection();
        if (!selection) {
            return;
        }

        if (selectionRef.current) {
            selection.removeAllRanges();
            selection.addRange(selectionRef.current);
        }
    };

    const syncValueFromEditor = () => {
        if (!editorRef.current) {
            return value;
        }
        const nextValue = htmlToTelegramTags(editorRef.current.innerHTML);
        if (onChange) {
            onChange(nextValue);
        }
        return nextValue;
    };

    const focusEditor = () => {
        if (editorRef.current) {
            editorRef.current.focus();
        }
    };

    const ensureSelection = () => {
        focusEditor();
        restoreSelection();
    };

    const insertTextAtSelection = (text) => {
        if (!editorRef.current) {
            return;
        }
        ensureSelection();

        const selection = window.getSelection();
        if (!selection || selection.rangeCount === 0) {
            editorRef.current.appendChild(document.createTextNode(text));
            syncValueFromEditor();
            return;
        }

        const range = selection.getRangeAt(0);
        if (!editorRef.current.contains(range.commonAncestorContainer)) {
            editorRef.current.appendChild(document.createTextNode(text));
            syncValueFromEditor();
            return;
        }

        range.deleteContents();
        const textNode = document.createTextNode(text);
        range.insertNode(textNode);
        range.setStartAfter(textNode);
        range.collapse(true);
        selection.removeAllRanges();
        selection.addRange(range);
        syncValueFromEditor();
    };

    const wrapSelectionWithSpoiler = () => {
        if (!editorRef.current) {
            return;
        }
        ensureSelection();

        const selection = window.getSelection();
        if (!selection || selection.rangeCount === 0) {
            return;
        }

        const range = selection.getRangeAt(0);
        if (!editorRef.current.contains(range.commonAncestorContainer)) {
            return;
        }

        const spoiler = document.createElement('span');
        spoiler.setAttribute('data-tg-spoiler', 'true');
        spoiler.className = 'telegram-spoiler';

        if (range.collapsed) {
            spoiler.appendChild(document.createTextNode(ZERO_WIDTH));
            range.insertNode(spoiler);
            const caretRange = document.createRange();
            caretRange.setStart(spoiler.firstChild, 1);
            caretRange.collapse(true);
            selection.removeAllRanges();
            selection.addRange(caretRange);
        } else {
            spoiler.appendChild(range.extractContents());
            range.insertNode(spoiler);
            const caretRange = document.createRange();
            caretRange.selectNodeContents(spoiler);
            caretRange.collapse(false);
            selection.removeAllRanges();
            selection.addRange(caretRange);
        }

        syncValueFromEditor();
    };

    const applyCommand = (command, commandValue) => {
        ensureSelection();
        if (document.execCommand) {
            document.execCommand(command, false, commandValue);
            captureSelection();
            syncValueFromEditor();
        }
    };

    const insertVariable = (val) => {
        insertTextAtSelection(val);
    };

    const insertTag = (tag) => {
        switch (tag) {
            case 'b':
                applyCommand('bold');
                break;
            case 'i':
                applyCommand('italic');
                break;
            case 'u':
                applyCommand('underline');
                break;
            case 's':
                applyCommand('strikeThrough');
                break;
            case 'blockquote':
                applyCommand('formatBlock', 'blockquote');
                break;
            case 'tg-spoiler':
                wrapSelectionWithSpoiler();
                break;
            default:
                break;
        }
    };

    return (
        <div>
            <div className="editor-toolbar mb-2 flex gap-2 max-w-[50px]">
                <button type="button" className="px-2 py-2 text-xs rounded-xl hover:bg-purple-900" title="Negrito" onMouseDown={(event) => event.preventDefault()} onClick={() => insertTag('b')}><i className="fas fa-bold"></i></button>
                <button type="button" className="px-2 py-2 text-xs rounded-xl hover:bg-purple-900" title="Itálico" onMouseDown={(event) => event.preventDefault()} onClick={() => insertTag('i')}><i className="fas fa-italic"></i></button>
                <button type="button" className="px-2 py-2 text-xs rounded-xl hover:bg-purple-900" title="Sublinhado" onMouseDown={(event) => event.preventDefault()} onClick={() => insertTag('u')}><i className="fas fa-underline"></i></button>
                <button type="button" className="px-2 py-2 text-xs rounded-xl hover:bg-purple-900" title="Tachado" onMouseDown={(event) => event.preventDefault()} onClick={() => insertTag('s')}><i className="fas fa-strikethrough"></i></button>
                {/* <button type="button" className="px-2 py-2 text-xs rou-xl hover:bg-purple-900nded" title="Código" onClick={() => insertTag('code')}><i className="fas fa-code"></i></button> */}
                <button type="button" className="px-2 py-2 text-xs rounded-xl hover:bg-purple-900" title="Spoiler" onMouseDown={(event) => event.preventDefault()} onClick={() => insertTag('tg-spoiler')}><i className="fas fa-eye-slash"></i></button>
                <button type="button" className="px-2 py-2 text-xs rounded-xl hover:bg-purple-900" title="Citação" onMouseDown={(event) => event.preventDefault()} onClick={() => insertTag('blockquote')}><i className="fas fa-quote-left"></i></button>
            </div>

            <div
                ref={editorRef}
                className="spt-textarea telegram-formatted"
                contentEditable
                role="textbox"
                tabIndex={0}
                suppressContentEditableWarning
                onInput={syncValueFromEditor}
                onFocus={() => {
                    isFocusedRef.current = true;
                    captureSelection();
                }}
                onKeyUp={captureSelection}
                onMouseUp={captureSelection}
                onBlur={() => {
                    isFocusedRef.current = false;
                    syncValueFromEditor();
                }}
                data-placeholder={placeholder}
            />

            <small className="form-hint">{hint}</small>


            {showVariables && (
                <>

                    <div className="block items-center gap-2 mt-3 md:flex">
                        <span className="form-hint">Variáveis Disponíveis (clique para inserir):</span>
                        <div className="flex mt-4 gap-2 md:m-0">

                            {variables.length === 0 && (
                                <>
                                    <button type="button" onClick={() => insertVariable('{nome}')} className="input3 px-2 py-1 text-xs rounded hover:border-[#8b5cf6]" title="Inserir {nome}">{'{nome}'}</button>
                                    <button type="button" onClick={() => insertVariable('{cidade}')} className="input3 px-2 py-1 text-xs rounded hover:border-[#8b5cf6]" title="Inserir {cidade}">{'{cidade}'}</button>
                                    <button type="button" onClick={() => insertVariable('{estado}')} className="input3 px-2 py-1 text-xs rounded hover:border-[#8b5cf6]" title="Inserir {estado}">{'{estado}'}</button>
                                    <button type="button" onClick={() => insertVariable('{pais}')} className="input3 px-2 py-1 text-xs rounded hover:border-[#8b5cf6]" title="Inserir {pais}">{'{pais}'}</button>
                                </>
                            )}
                            {variables.map((variable, index) => (
                                <button
                                    key={index}
                                    type="button"
                                    onClick={() => insertVariable(`{${variable}}`)}
                                    className="input3 px-2 py-1 text-xs rounded hover:border-[#8b5cf6]"
                                    title={`Inserir {${variable}}`}
                                >
                                    {`{${variable}}`}
                                </button>
                            ))}
                        </div>
                    </div>

                    {variables.length === 0 && (
                        <span className="form-hint">
                            <span style={{ color: '#eab308' }}>
                                <i className="fas fa-exclamation-triangle me-1 mt-4" />
                                Atenção:{' '}
                            </span>
                            Para usar {'{cidade}'}, {'{estado}'} e {'{pais}'} você deve utilizar os{' '}
                            <a href="/redirecionadores" className="sx-link">Redirecionadores</a>.
                        </span>

                    )}
                </>)
            }
        </div>
    );
}

export default TextForm;
